import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons/faShareNodes";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { Button } from "@wondermaps/ui";
import { analytics } from "../../lib/analytics";

interface ShareButtonProps {
  /**
   * URL to share. Defaults to `window.location.href` AS-IS (search params preserved).
   * Callers that want canonical pathname-only must compute and pass it.
   */
  url?: string;
  title?: string;
  text?: string;
  className?: string;
  brandAccent?: string | null;
}

/**
 * Visitor share button. Tries navigator.share (mobile), falls back to clipboard.
 * On clipboard success, swaps the share icon for a green check for 2s — that
 * IS the success feedback, no toast.
 */
export function ShareButton({
  url,
  title,
  text,
  className,
  brandAccent,
}: ShareButtonProps) {
  const { t } = useTranslation("map");
  const { t: tCommon } = useTranslation("common");
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const shareUrl = url ?? window.location.href;
    const shareData = {
      title: title ?? tCommon("share.defaultTitle"),
      text: text ?? tCommon("share.defaultText"),
      url: shareUrl,
    };

    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
        analytics.shareMap({ method: "native" });
        return;
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      analytics.shareMap({ method: "clipboard" });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleShare}
      aria-label={copied ? tCommon("share.linkCopied") : t("nav.share")}
      className={className}
    >
      {copied ? (
        <FontAwesomeIcon
          icon={faCheck}
          className={brandAccent ? "h-4 w-4" : "h-4 w-4 text-green-600"}
          style={brandAccent ? { color: brandAccent } : undefined}
          aria-hidden
        />
      ) : (
        <FontAwesomeIcon
          icon={faShareNodes}
          className="h-4 w-4 text-foreground"
          style={brandAccent ? { color: brandAccent } : undefined}
          aria-hidden
        />
      )}
    </Button>
  );
}
